import {
  Shield, Car, Users, Calendar, DollarSign,
  LogOut, FileText, User, Menu, TrendingUp,
  UserCog, Briefcase, Mail, CreditCard, Image, UserPlus, Contact, Eye, Coins, BarChart3,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { SidebarLogo } from "./LogoVariants";

var ADMIN_EMAIL = "admin@methoscorretora.com.br";
var ROLE_LABELS = { admin: "Gestor", team_leader: "Lider de Equipe", broker: "Corretor", assistant: "Assistente" };

function getInitials(name) {
  if (!name) return "U";
  var parts = name.split(" ");
  return ((parts[0] ? parts[0][0] : "") + (parts[1] ? parts[1][0] : "")).toUpperCase();
}

function navCls(active) {
  if (active) return "flex items-center gap-2.5 px-3 py-1.5 rounded-lg bg-[#3B82F6] text-white cursor-pointer shadow-lg shadow-blue-500/20";
  return "flex items-center gap-2.5 px-3 py-1.5 rounded-lg text-white/60 hover:bg-white/5 cursor-pointer transition-colors";
}

function CategoryLabel({ label }) {
  return (
    <div className="pt-2.5 mt-1 border-t border-white/10 first:pt-0 first:mt-0 first:border-t-0">
      <p className="px-3 text-white/40 text-[9px] font-semibold uppercase tracking-wider mb-0.5">{label}</p>
    </div>
  );
}

function hasPerm(user, mod) {
  if (!user) return false;
  var role = user.role;
  if (role === "admin") return true;
  if (role === "assistant") {
    var perms = user.permissions || {};
    var mp = perms[mod] || {};
    return !!mp.view;
  }
  return true;
}

function SidebarNav({ viewMode, onViewChange, user, isAdmin, onLogout, activePage }) {
  var navigate = useNavigate();
  var initials = getInitials(user ? user.name : null);
  var isTeamLeader = user ? user.role === "team_leader" : false;
  var isAssistant = user ? user.role === "assistant" : false;
  var isSuperAdmin = user ? user.email === ADMIN_EMAIL : false;
  var roleLbl = ROLE_LABELS[user ? user.role : "broker"] || "Corretor";

  var showGestao = isAdmin || isTeamLeader;
  var showMarketing = isAdmin;

  return (
    <aside className="hidden lg:flex w-64 xl:w-72 bg-[#0A2F3D] flex-col fixed h-full z-20">
      <div className="p-4 border-b border-white/10"><SidebarLogo /></div>
      <div className="px-3 py-2.5 border-b border-white/10">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-full bg-[#3B82F6] flex items-center justify-center ring-2 ring-[#F26A4B]/30">
            <span className="text-white font-semibold text-xs">{initials}</span>
          </div>
          <div>
            <p className="text-white font-medium text-xs">{user ? user.name : ""}</p>
            <p className="text-[#F26A4B] text-[10px] font-medium">{roleLbl}</p>
          </div>
        </div>
      </div>
      {(isAdmin || isAssistant) && (
        <div className="px-3 pt-2.5" data-testid="view-toggle-container">
          <div className="bg-white/5 rounded-lg p-0.5 flex gap-0.5">
            <button data-testid="view-toggle-gestor" onClick={function() { onViewChange("gestor"); }}
              className={"flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-medium transition-all " + (viewMode === "gestor" ? "bg-[#3B82F6] text-white shadow-lg" : "text-white/50 hover:text-white/80")}>
              <Briefcase className="w-3 h-3" />{isAssistant ? "Assistente" : "Gestor"}
            </button>
            <button data-testid="view-toggle-corretor" onClick={function() { onViewChange("corretor"); }}
              className={"flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-medium transition-all " + (viewMode === "corretor" ? "bg-[#F26A4B] text-white shadow-lg" : "text-white/50 hover:text-white/80")}>
              <User className="w-3 h-3" />Corretor
            </button>
          </div>
        </div>
      )}
      <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
        <CategoryLabel label="Principal" />
        <div className={navCls(activePage === "dashboard")} data-testid="nav-dashboard" onClick={function() { navigate("/painel"); }}>
          <BarChart3 className="w-4 h-4" /><span className="font-medium text-sm">Dashboard</span>
        </div>

        <CategoryLabel label="Comercial" />
        {hasPerm(user, "leads") && (
          <div className={navCls(activePage === "leads")} data-testid="nav-leads" onClick={function() { navigate("/leads"); }}>
            <Users className="w-4 h-4" /><span className="font-medium text-sm">Leads</span>
          </div>
        )}
        {hasPerm(user, "leads") && (
          <div className={navCls(activePage === "funil-leads")} data-testid="nav-funil-leads" onClick={function() { navigate("/funil-leads"); }}>
            <TrendingUp className="w-4 h-4" /><span className="font-medium text-sm">Funil de Vendas</span>
          </div>
        )}
        {hasPerm(user, "clientes") && (
          <div className={navCls(activePage === "clientes")} data-testid="nav-clientes" onClick={function() { navigate("/clientes"); }}>
            <Contact className="w-4 h-4" /><span className="font-medium text-sm">Clientes</span>
          </div>
        )}

        <CategoryLabel label="Produtos" />
        {hasPerm(user, "consorcios") && (
          <div className={navCls(activePage === "consorcios")} data-testid="nav-consorcios" onClick={function() { navigate("/consorcios"); }}>
            <Car className="w-4 h-4" /><span className="font-medium text-sm">Consórcios</span>
          </div>
        )}
        {hasPerm(user, "seguros") && (
          <div className={navCls(activePage === "seguros")} data-testid="nav-seguros" onClick={function() { navigate("/seguros"); }}>
            <Shield className="w-4 h-4" /><span className="font-medium text-sm">Seguros</span>
          </div>
        )}

        <CategoryLabel label="Financeiro" />
        {hasPerm(user, "comissoes") && (
          <div className={navCls(activePage === "comissoes")} data-testid="nav-comissoes" onClick={function() { navigate("/comissoes"); }}>
            <DollarSign className="w-4 h-4" /><span className="font-medium text-sm">Comissoes</span>
          </div>
        )}
        {hasPerm(user, "pagamentos") && (
          <div className={navCls(activePage === "pagamentos")} data-testid="nav-pagamentos" onClick={function() { navigate("/pagamentos"); }}>
            <CreditCard className="w-4 h-4" /><span className="font-medium text-sm">Pagamentos</span>
          </div>
        )}

        {(showMarketing || isAssistant) && (hasPerm(user, "comunicacao") || hasPerm(user, "banners")) && (
          <div>
            <CategoryLabel label="Marketing" />
            {hasPerm(user, "comunicacao") && (
              <div className={navCls(activePage === "comunicacao")} data-testid="nav-comunicacao" onClick={function() { navigate("/comunicacao"); }}>
                <Mail className="w-4 h-4" /><span className="font-medium text-sm">Comunicacao</span>
              </div>
            )}
            {hasPerm(user, "banners") && (
              <div className={navCls(activePage === "banners")} data-testid="nav-banners" onClick={function() { navigate("/banners"); }}>
                <Image className="w-4 h-4" /><span className="font-medium text-sm">Banners</span>
              </div>
            )}
          </div>
        )}
        {hasPerm(user, "indicacoes") && (showGestao || isAssistant) && (
          <div className={navCls(activePage === "indicacoes")} data-testid="nav-indicacoes" onClick={function() { navigate("/indicacoes"); }}>
            <UserPlus className="w-4 h-4" /><span className="font-medium text-sm">Indicacoes</span>
          </div>
        )}

        <CategoryLabel label="Gestao" />
        {hasPerm(user, "equipe") && (showGestao || isAssistant) && (
          <div className={navCls(activePage === "equipe")} data-testid="nav-equipe" onClick={function() { navigate("/equipe"); }}>
            <UserCog className="w-4 h-4" /><span className="font-medium text-sm">Equipe</span>
          </div>
        )}
        {hasPerm(user, "relatorios") && (showGestao || isAssistant) && (
          <div className={navCls(activePage === "relatorios")} data-testid="nav-relatorios" onClick={function() { navigate("/relatorios"); }}>
            <FileText className="w-4 h-4" /><span className="font-medium text-sm">Relatorios</span>
          </div>
        )}
        {hasPerm(user, "agenda") && (
          <div className={navCls(activePage === "agenda")} data-testid="nav-agenda" onClick={function() { navigate("/agenda"); }}>
            <Calendar className="w-4 h-4" /><span className="font-medium text-sm">Agenda</span>
          </div>
        )}
        {hasPerm(user, "doletas") && isAdmin && (
          <div className={navCls(activePage === "doletas")} data-testid="nav-doletas" onClick={function() { navigate("/doletas"); }}>
            <Coins className="w-4 h-4" /><span className="font-medium text-sm">DOLETAS</span>
          </div>
        )}

        {isSuperAdmin && (
          <div>
            <CategoryLabel label="Sistema" />
            <div className={navCls(activePage === "vigia")} data-testid="nav-vigia" onClick={function() { navigate("/vigia"); }}>
              <Eye className="w-4 h-4" /><span className="font-medium text-sm">VIGIA</span>
            </div>
          </div>
        )}
      </nav>
      <div className="p-3 border-t border-white/10">
        <button onClick={onLogout} className="w-full flex items-center gap-2.5 px-3 py-1.5 rounded-lg text-white/60 hover:bg-red-500/10 hover:text-red-400 transition-colors" data-testid="sidebar-logout-button">
          <LogOut className="w-4 h-4" /><span className="font-medium text-sm">Sair</span>
        </button>
      </div>
    </aside>
  );
}

export { SidebarNav };
